import React, { createContext, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ReasoningSection as ReasoningSectionType } from '../../../../../types/case-workspace';
import {
  getDecisionReferenceFromHref,
  preprocessReasoningMarkdown,
} from './utils/markdownParser';
import ReportSectionHeading from './ReportSectionHeading';

interface ReasoningSectionProps {
  reasoning: ReasoningSectionType;
  onDecisionClick?: (decisionIdOrReference: string) => void;
}

const ListItemContext = createContext(false);

const MarkdownParagraph: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isInsideListItem = useContext(ListItemContext);

  if (isInsideListItem) {
    return <span className="text-sm text-foreground leading-relaxed">{children}</span>;
  }

  return <p className="text-sm text-foreground leading-relaxed">{children}</p>;
};

function scrollToDecisionReference(reference: string) {
  const element = document.getElementById(`precedent-${reference}`);
  if (!element) {
    return;
  }

  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');

  setTimeout(() => {
    element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
  }, 2000);
}

const ReasoningSection: React.FC<ReasoningSectionProps> = ({ reasoning, onDecisionClick }) => {
  const reasoningMarkdown = preprocessReasoningMarkdown(reasoning.narrative);

  return (
    <section className="border-b border-border px-6 py-5">
      <ReportSectionHeading number={3} title="Guidance" className="mb-4" />

      <div className="rounded-lg border border-[var(--go-warm-border)] bg-background p-4">
        <div className="prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-p:text-[13px] prose-p:leading-[1.7] prose-strong:text-foreground prose-strong:font-semibold prose-ul:my-3 prose-ol:my-3 prose-li:my-1">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => <h3 className="text-base font-semibold text-foreground mt-5 mb-2">{children}</h3>,
            h2: ({ children }) => <h3 className="text-base font-semibold text-foreground mt-5 mb-2">{children}</h3>,
            h3: ({ children }) => <h4 className="text-sm font-semibold text-foreground mt-4 mb-1">{children}</h4>,
            h4: ({ children }) => <h4 className="text-sm font-semibold text-foreground mt-4 mb-1">{children}</h4>,
            p: ({ children }) => <MarkdownParagraph>{children}</MarkdownParagraph>,
            ul: ({ children }) => <ul className="list-disc list-inside space-y-1 text-sm text-foreground pl-1">{children}</ul>,
            ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 text-sm text-foreground pl-1">{children}</ol>,
            li: ({ children }) => (
              <li className="leading-relaxed">
                <ListItemContext.Provider value={true}>{children}</ListItemContext.Provider>
              </li>
            ),
            a: ({ href, children }) => {
              const decisionReference = getDecisionReferenceFromHref(href);

              if (decisionReference) {
                if (onDecisionClick) {
                  return (
                    <button
                      onClick={() => onDecisionClick(decisionReference)}
                      className="text-primary hover:underline cursor-pointer"
                    >
                      {children}
                    </button>
                  );
                }

                return (
                  <a
                    href={`#precedent-${decisionReference}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToDecisionReference(decisionReference);
                    }}
                    className="text-primary hover:underline"
                  >
                    {children}
                  </a>
                );
              }

              return (
                <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  {children}
                </a>
              );
            },
          }}
        >
          {reasoningMarkdown}
        </ReactMarkdown>
        </div>
      </div>

      {/* References (if provided) */}
      {reasoning.references && reasoning.references.length > 0 && (
        <div className="mt-4 rounded-lg border border-border bg-[var(--go-spring)] p-4">
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">References</h4>
          <div className="flex flex-wrap gap-2">
            {reasoning.references
              .filter(ref => ref && typeof ref === 'string' && ref.startsWith('DRN-'))
              .map((ref) => (
                onDecisionClick ? (
                  <button
                    key={ref}
                    onClick={() => onDecisionClick(ref)}
                    className="text-xs text-primary hover:underline cursor-pointer"
                  >
                    {ref}
                  </button>
                ) : (
                  <a
                    key={ref}
                    href={`#precedent-${ref}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToDecisionReference(ref);
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    {ref}
                  </a>
                )
              ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default ReasoningSection;
